/**
 * Buylist estimator + request form (sections/buylist-request.liquid).
 *
 * - Searches the storefront's predictive search (/search/suggest.json) and reads a product's variants from
 *   /products/<handle>.js, so no app is needed. The variant price is used as the item's value.
 * - Shows an estimated offer RANGE per item and in total. The percentages live in the section settings, are handed to
 *   this script as JSON and are never printed.
 * - Multi-select categories reveal a details panel each. On submit everything (items, estimate, payout choice, details)
 *   is written into the contact form's body field, so a normal Shopify contact form emails it to the store.
 * - The list is kept in sessionStorage so a reload doesn't lose it.
 */

const STORAGE_KEY = 'opcg-buylist-v1';
const MAX_QTY = 999;

const escapeHtml = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]
  );

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Maps a Shopify product type (e.g. "One Piece Single", "Pokemon Sealed") onto a buylist category key.
 * @param {string} type
 * @returns {string}
 */
function categoryFromType(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('japanese')) return 'japanese';
  if (t.includes('sealed')) return 'sealed';
  if (t.includes('accessor')) return 'accessories';
  if (t.includes('one piece') && t.includes('single')) return 'one_piece_singles';
  if (/pok[eé]mon/.test(t) && t.includes('single')) return 'pokemon_singles';
  return 'other';
}

class BuylistRequest extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('script[data-bl-config]');
    try {
      this.config = JSON.parse(configEl?.textContent ?? '');
    } catch {
      return;
    }

    this.abort = new AbortController();
    this.state = { items: [], payout: 'cash', mix: 50, cats: [] };
    this.results = [];
    this.active = -1;
    this.categoryLabels = Object.fromEntries(this.config.categories.map((c) => [c.key, c.label]));
    this.rates = this.buildRates();
    this.money = this.buildFormatter();

    this.refs = {
      payout: this.querySelector('[data-bl-payout]'),
      mix: this.querySelector('[data-bl-mix]'),
      mixInput: this.querySelector('[data-bl-mix-input]'),
      mixOut: this.querySelector('[data-bl-mix-out]'),
      search: this.querySelector('[data-bl-search]'),
      input: this.querySelector('[data-bl-input]'),
      resultsList: this.querySelector('[data-bl-results]'),
      status: this.querySelector('[data-bl-status]'),
      manualToggle: this.querySelector('[data-bl-manual-toggle]'),
      manual: this.querySelector('[data-bl-manual]'),
      manualName: this.querySelector('[data-bl-manual-name]'),
      manualCategory: this.querySelector('[data-bl-manual-category]'),
      manualPrice: this.querySelector('[data-bl-manual-price]'),
      manualQty: this.querySelector('[data-bl-manual-qty]'),
      manualAdd: this.querySelector('[data-bl-manual-add]'),
      manualError: this.querySelector('[data-bl-manual-error]'),
      items: this.querySelector('[data-bl-items]'),
      empty: this.querySelector('[data-bl-empty]'),
      totals: this.querySelector('[data-bl-totals]'),
      totalsLabel: this.querySelector('[data-bl-totals-label]'),
      totalsValue: this.querySelector('[data-bl-totals-value]'),
      totalsCompare: this.querySelector('[data-bl-totals-compare]'),
      continueCta: this.querySelector('[data-bl-continue]'),
      form: this.querySelector('form'),
      chips: [...this.querySelectorAll('[data-bl-cat]')],
      panels: [...this.querySelectorAll('[data-bl-panel]')],
      portalNote: this.querySelector('[data-bl-portal-note]'),
      summary: this.querySelector('[data-bl-summary]'),
      body: this.querySelector('[data-bl-body]'),
      error: this.querySelector('[data-bl-error]'),
      submit: this.querySelector('[data-bl-submit]'),
    };

    const success = this.querySelector('[data-bl-success]');
    if (success) {
      this.clearStored();
      requestAnimationFrame(() => success.scrollIntoView({ block: 'center' }));
    } else {
      this.restore();
    }

    this.bind();
    this.renderAll();
    if (!success) this.prefillFromUrl();
  }

  /**
   * Starts with a product already on the list when the visitor arrives from a product page's "Sell this card to us"
   * button (?sell=<product handle>). The parameter is removed from the address afterwards so a reload doesn't add it
   * again, and a product that is already on the list is left alone.
   */
  async prefillFromUrl() {
    let handle = '';
    try {
      const url = new URL(window.location.href);
      handle = url.searchParams.get('sell') ?? '';
      if (!handle) return;
      url.searchParams.delete('sell');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch {
      return;
    }

    if (!/^[\w.-]+$/.test(handle)) return;
    if (this.state.items.some((item) => item.handle === handle)) return;

    try {
      const base = this.config.productBase.replace(/\/$/, '');
      const response = await fetch(`${base}/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.abort?.signal,
      });
      if (!response.ok) return;

      const data = await response.json();
      const item = await this.itemFromProduct({
        handle: data.handle,
        title: data.title,
        type: data.type,
        price: round2(Number(data.price) / 100),
        featured_image: { url: data.featured_image || '' },
      });
      if (this.addItem(item)) this.setStatus(`Added ${data.title} to your list.`);
    } catch {
      // The page still works: the visitor can search for the card themselves
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.searchAbort?.abort();
    clearTimeout(this.debounce);
  }

  /* ---------- Setup ---------- */

  buildRates() {
    const pct = (value, fallback) => {
      const n = Number(value);
      return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : fallback;
    };
    const normalize = (r) => {
      const cashLow = pct(r.cashLow, 65);
      const cashHigh = pct(r.cashHigh, 85);
      const creditLow = pct(r.creditLow, 75);
      const creditHigh = pct(r.creditHigh, 90);
      return {
        cashLow: Math.min(cashLow, cashHigh) / 100,
        cashHigh: Math.max(cashLow, cashHigh) / 100,
        creditLow: Math.min(creditLow, creditHigh) / 100,
        creditHigh: Math.max(creditLow, creditHigh) / 100,
      };
    };

    const { default: base, overrides } = this.config.rates;
    return {
      base: normalize(base),
      overrides: Object.fromEntries(Object.entries(overrides ?? {}).map(([key, value]) => [key, normalize(value)])),
    };
  }

  buildFormatter() {
    try {
      return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
    } catch {
      return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });
    }
  }

  bind() {
    const { refs } = this;
    const on = (target, type, handler) => target?.addEventListener(type, handler, { signal: this.abort.signal });

    // Payout choice
    on(refs.payout, 'change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      this.state.payout = event.target.value;
      this.save();
      this.renderPayout();
      this.updateNumbers();
    });
    // The bar only moves on "a mix of both". In cash and credit it is locked to one end.
    on(refs.mixInput, 'input', () => {
      if (this.state.payout !== 'mixed') {
        this.renderPayout(); // snap back to the pinned end
        return;
      }
      this.state.mix = Number(refs.mixInput.value) || 50;
      this.save();
      this.renderPayout();
      this.updateNumbers();
    });

    // Search
    on(refs.input, 'input', () => this.onSearchInput());
    on(refs.input, 'keydown', (event) => this.onSearchKeydown(event));
    on(refs.input, 'focus', () => {
      if (this.results.length && refs.input.value.trim().length >= 2) this.openResults();
    });
    on(refs.resultsList, 'mousedown', (event) => event.preventDefault());
    on(refs.resultsList, 'click', (event) => {
      const option = event.target.closest('[data-index]');
      if (option) this.chooseResult(Number(option.dataset.index));
    });
    on(document, 'click', (event) => {
      if (!refs.search.contains(event.target)) this.closeResults();
    });

    // Manual entry
    on(refs.manualToggle, 'click', () => {
      const open = refs.manual.hidden;
      refs.manual.hidden = !open;
      refs.manualToggle.setAttribute('aria-expanded', String(open));
      if (open) refs.manualName.focus();
    });
    on(refs.manualAdd, 'click', () => this.addManual());
    on(refs.manual, 'keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('input')) {
        event.preventDefault();
        this.addManual();
      }
    });

    // Item rows
    on(refs.items, 'input', (event) => this.onItemInput(event));
    on(refs.items, 'change', (event) => this.onItemChange(event));
    on(refs.items, 'click', (event) => {
      const button = event.target.closest('[data-bl-remove]');
      if (button) this.removeItem(button.closest('[data-uid]')?.dataset.uid);
    });

    // Categories + form
    on(this.querySelector('[data-bl-chips]'), 'change', () => {
      this.state.cats = refs.chips.filter((chip) => chip.checked).map((chip) => chip.value);
      this.renderChips();
    });
    on(refs.form, 'submit', (event) => this.onSubmit(event));
    on(window, 'pageshow', () => this.resetSubmit());
  }

  /* ---------- State ---------- */

  get share() {
    const { payout, mix } = this.state;
    if (payout === 'credit') return 1;
    if (payout === 'cash') return 0;
    return mix / 100;
  }

  save() {
    try {
      // Category chips are deliberately not saved: every visit starts with nothing ticked
      const { items, payout, mix } = this.state;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ items, payout, mix }));
    } catch {
      // Storage can be blocked (private windows); the page still works without it
    }
  }

  clearStored() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  restore() {
    try {
      const data = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
      if (!data || !Array.isArray(data.items)) return;

      const known = new Set(this.config.categories.map((c) => c.key));
      this.state.items = data.items.slice(0, this.config.maxItems).flatMap((raw) => {
        const price = Number(raw?.price);
        const qty = Math.round(Number(raw?.qty));
        if (!raw?.title || !Number.isFinite(price) || !Number.isFinite(qty)) return [];
        return [
          {
            uid: String(raw.uid || this.newUid()).replace(/[^\w-]/g, ''),
            handle: /^[\w.-]*$/.test(raw.handle ?? '') ? raw.handle ?? '' : '',
            title: String(raw.title).slice(0, 200),
            image: /^https?:\/\//.test(raw.image ?? '') ? raw.image : '',
            category: known.has(raw.category) ? raw.category : 'other',
            variants: Array.isArray(raw.variants)
              ? raw.variants.map((v) => ({ id: Number(v.id) || 0, title: String(v.title ?? '').slice(0, 100), price: Math.max(0, Number(v.price) || 0) }))
              : [],
            variantId: Number(raw.variantId) || 0,
            price: Math.max(0, price),
            qty: Math.min(MAX_QTY, Math.max(1, qty)),
            source: raw.source === 'manual' ? 'manual' : 'store',
          },
        ];
      });
      if (['cash', 'credit', 'mixed'].includes(data.payout)) this.state.payout = data.payout;
      const mix = Number(data.mix);
      if (mix >= 10 && mix <= 90) this.state.mix = mix;
    } catch {
      // Ignore a corrupt saved list and start fresh
    }
  }

  newUid() {
    return `bl-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  }

  /* ---------- Estimates ---------- */

  ratesFor(category) {
    return this.rates.overrides[category] ?? this.rates.base;
  }

  /** Estimated offer for one line at a given credit share (0 = all cash, 1 = all store credit). */
  estimate(item, share) {
    const rates = this.ratesFor(item.category);
    const value = Math.max(0, Number(item.price) || 0) * item.qty;
    return {
      low: round2(value * (rates.cashLow * (1 - share) + rates.creditLow * share)),
      high: round2(value * (rates.cashHigh * (1 - share) + rates.creditHigh * share)),
    };
  }

  totals(share) {
    const sum = this.state.items.reduce(
      (acc, item) => {
        const { low, high } = this.estimate(item, share);
        return { low: acc.low + low, high: acc.high + high };
      },
      { low: 0, high: 0 }
    );
    return { low: round2(sum.low), high: round2(sum.high) };
  }

  formatRange({ low, high }) {
    if (!(high > 0)) return '—';
    return low === high ? this.money.format(low) : `${this.money.format(low)} – ${this.money.format(high)}`;
  }

  payoutName() {
    const { payout, mix } = this.state;
    if (payout === 'cash') return 'Cash';
    if (payout === 'credit') return 'Store credit';
    return `A mix of both (about ${mix}% store credit)`;
  }

  /* ---------- Search ---------- */

  onSearchInput() {
    const query = this.refs.input.value.trim();
    clearTimeout(this.debounce);
    if (query.length < 2) {
      this.searchAbort?.abort();
      this.results = [];
      this.closeResults();
      this.setStatus('');
      return;
    }
    this.debounce = setTimeout(() => this.search(query), 250);
  }

  async search(query) {
    this.searchAbort?.abort();
    const controller = new AbortController();
    this.searchAbort = controller;

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,tag',
    });

    this.setStatus('Searching…');
    try {
      const response = await fetch(`${this.config.searchUrl}?${params}`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Search failed (${response.status})`);
      const data = await response.json();
      this.results = data?.resources?.results?.products ?? [];
      this.renderResults(query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.results = [];
      this.closeResults();
      this.setStatus("Search isn't available right now. You can add your item yourself instead.");
    }
  }

  renderResults(query) {
    const { resultsList } = this.refs;
    if (!this.results.length) {
      this.closeResults();
      this.setStatus(`No matches for "${query}". Try fewer words, or add it yourself.`);
      return;
    }

    resultsList.innerHTML = this.results
      .map((product, index) => {
        const image = this.thumb(product.featured_image?.url || product.image || '');
        const min = Number(product.price_min ?? product.price);
        const max = Number(product.price_max ?? product.price);
        const price = min === max ? this.money.format(min) : `${this.money.format(min)} – ${this.money.format(max)}`;
        return `
          <li class="bl-option" role="option" id="${this.optionId(index)}" data-index="${index}" aria-selected="false">
            ${image ? `<img class="bl-option__img" src="${escapeHtml(image)}" alt="" width="44" height="44" loading="lazy">` : '<span class="bl-option__img"></span>'}
            <span class="bl-option__main">
              <span class="bl-option__title">${escapeHtml(product.title)}</span>
              <span class="bl-option__meta">${escapeHtml(product.type || 'Product')} · ${escapeHtml(price)}</span>
            </span>
          </li>`;
      })
      .join('');

    this.active = -1;
    this.openResults();
    const count = this.results.length;
    this.setStatus(`${count} result${count === 1 ? '' : 's'}. Use the arrow keys or tap one to add it.`);
  }

  optionId(index) {
    return `${this.refs.resultsList.id}-${index}`;
  }

  openResults() {
    this.refs.resultsList.hidden = false;
    this.refs.input.setAttribute('aria-expanded', 'true');
  }

  closeResults() {
    this.refs.resultsList.hidden = true;
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.input.removeAttribute('aria-activedescendant');
    this.active = -1;
  }

  moveActive(step) {
    const options = [...this.refs.resultsList.querySelectorAll('[role="option"]')];
    if (!options.length) return;
    this.active = (this.active + step + options.length) % options.length;
    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
    const current = options[this.active];
    this.refs.input.setAttribute('aria-activedescendant', current.id);
    current.scrollIntoView?.({ block: 'nearest' });
  }

  onSearchKeydown(event) {
    const open = !this.refs.resultsList.hidden;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open && this.results.length) this.openResults();
        else this.moveActive(1);
        break;
      case 'ArrowUp':
        if (open) {
          event.preventDefault();
          this.moveActive(-1);
        }
        break;
      case 'Enter':
        if (open) {
          event.preventDefault();
          this.chooseResult(Math.max(0, this.active));
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          this.closeResults();
        }
        break;
    }
  }

  async chooseResult(index) {
    const product = this.results[index];
    if (!product) return;

    this.closeResults();
    this.refs.input.value = '';
    this.results = [];
    this.setStatus('Adding…');

    const item = await this.itemFromProduct(product);
    if (this.addItem(item)) this.setStatus(`Added ${product.title}.`);
    this.refs.input.focus();
  }

  /** Builds a list item from a search result, loading the product's variants (conditions) when possible. */
  async itemFromProduct(product) {
    let variants = [];
    try {
      const base = this.config.productBase.replace(/\/$/, '');
      const response = await fetch(`${base}/products/${encodeURIComponent(product.handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        // The product .js endpoint reports prices in cents
        variants = (data.variants ?? []).map((v) => ({
          id: v.id,
          title: v.title,
          price: round2(Number(v.price) / 100),
        }));
      }
    } catch {
      // Fall back to the search result's price below
    }
    if (!variants.length) {
      variants = [{ id: 0, title: 'Default Title', price: round2(Number(product.price) || 0) }];
    }

    return {
      uid: this.newUid(),
      handle: product.handle,
      title: product.title,
      image: product.featured_image?.url || product.image || '',
      category: categoryFromType(product.type),
      variants,
      variantId: variants[0].id,
      price: variants[0].price,
      qty: 1,
      source: 'store',
    };
  }

  thumb(url) {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=88`;
  }

  setStatus(message) {
    this.refs.status.textContent = message;
  }

  /* ---------- Items ---------- */

  addItem(item) {
    const { items } = this.state;
    const existing = items.find(
      (other) => other.source === 'store' && item.source === 'store' && other.handle === item.handle && other.variantId === item.variantId
    );

    if (existing) {
      existing.qty = Math.min(MAX_QTY, existing.qty + 1);
    } else if (items.length >= this.config.maxItems) {
      this.setStatus(
        `You can list up to ${this.config.maxItems} items here. For more, choose Collections & bulk below and add a link to photos.`
      );
      return false;
    } else {
      items.push(item);
    }

    this.save();
    this.renderAll();
    return true;
  }

  addManual() {
    const { manualName, manualCategory, manualPrice, manualQty, manualError } = this.refs;
    const name = manualName.value.trim();
    const price = parseFloat(manualPrice.value);
    const qty = Math.min(MAX_QTY, Math.max(1, parseInt(manualQty.value, 10) || 1));

    if (!name) {
      manualError.textContent = 'Give it a name.';
      manualName.focus();
      return;
    }
    if (!(price > 0)) {
      manualError.textContent = 'Enter what one is worth.';
      manualPrice.focus();
      return;
    }
    manualError.textContent = '';

    const added = this.addItem({
      uid: this.newUid(),
      handle: '',
      title: name,
      image: '',
      category: manualCategory.value,
      variants: [],
      variantId: 0,
      price: round2(price),
      qty,
      source: 'manual',
    });

    if (added) {
      manualName.value = '';
      manualPrice.value = '';
      manualQty.value = '1';
      this.setStatus(`Added ${name}.`);
      manualName.focus();
    }
  }

  findItem(uid) {
    return this.state.items.find((item) => item.uid === uid);
  }

  removeItem(uid) {
    this.state.items = this.state.items.filter((item) => item.uid !== uid);
    this.save();
    this.renderAll();
    this.refs.input.focus();
  }

  onItemInput(event) {
    const item = this.findItem(event.target.closest('[data-uid]')?.dataset.uid);
    if (!item) return;

    if (event.target.matches('[data-bl-price]')) {
      item.price = Math.max(0, parseFloat(event.target.value) || 0);
    } else if (event.target.matches('[data-bl-qty]')) {
      const qty = parseInt(event.target.value, 10);
      if (!Number.isFinite(qty)) return;
      item.qty = Math.min(MAX_QTY, Math.max(1, qty));
    } else {
      return;
    }

    this.save();
    this.updateNumbers();
  }

  onItemChange(event) {
    const row = event.target.closest('[data-uid]');
    const item = this.findItem(row?.dataset.uid);
    if (!item) return;

    if (event.target.matches('[data-bl-variant]')) {
      const variant = item.variants.find((v) => String(v.id) === event.target.value);
      if (!variant) return;
      item.variantId = variant.id;
      item.price = variant.price;
      this.save();
      this.renderAll();
      this.refs.items.querySelector(`[data-uid="${item.uid}"] [data-bl-variant]`)?.focus();
    } else if (event.target.matches('[data-bl-price]')) {
      event.target.value = item.price.toFixed(2);
    } else if (event.target.matches('[data-bl-qty]')) {
      event.target.value = String(item.qty);
    }
  }

  /* ---------- Rendering ---------- */

  renderAll() {
    this.renderPayout();
    this.renderItems();
    this.updateNumbers();
  }

  renderPayout() {
    const { payout, mix } = this.state;
    for (const radio of this.refs.payout.querySelectorAll('input[type="radio"]')) {
      radio.checked = radio.value === payout;
    }

    // Cash pins the bar at 100% cash and credit at 100% credit. Only "a mix of both" can be moved.
    const mixed = payout === 'mixed';
    const credit = mixed ? mix : payout === 'credit' ? 100 : 0;
    const { mixInput, mixOut } = this.refs;
    mixInput.min = mixed ? '10' : '0'; // set the bounds before the value so it is never clamped by the old mode
    mixInput.max = mixed ? '90' : '100';
    mixInput.value = String(credit);
    mixInput.disabled = !mixed;

    const text = mixed ? `${credit}% store credit, ${100 - credit}% cash` : credit === 100 ? '100% store credit' : '100% cash';
    mixOut.textContent = text;
    mixInput.setAttribute('aria-valuetext', text);
  }

  variantLabel(item) {
    const variant = item.variants.find((v) => v.id === item.variantId);
    return variant && variant.title !== 'Default Title' ? variant.title : '';
  }

  renderItems() {
    const { items } = this.state;
    const base = this.config.productBase.replace(/\/$/, '');

    this.refs.items.setAttribute('role', 'list');
    this.refs.items.innerHTML = items
      .map((item) => {
        const title = escapeHtml(item.title);
        const image = this.thumb(item.image);
        const variantControl =
          item.variants.length > 1
            ? `<select class="bl-input bl-row__variant" data-bl-variant aria-label="Condition for ${title}">
                ${item.variants
                  .map(
                    (v) =>
                      `<option value="${v.id}"${v.id === item.variantId ? ' selected' : ''}>${escapeHtml(v.title)} (${escapeHtml(this.money.format(v.price))})</option>`
                  )
                  .join('')}
              </select>`
            : '';

        return `
          <div class="bl-row" role="listitem" data-uid="${escapeHtml(item.uid)}">
            ${image ? `<img class="bl-row__img" src="${escapeHtml(image)}" alt="" width="44" height="44" loading="lazy">` : '<span class="bl-row__img"></span>'}
            <div class="bl-row__main">
              ${
                item.handle
                  ? `<a class="bl-row__title" href="${escapeHtml(base)}/products/${encodeURIComponent(item.handle)}" target="_blank" rel="noopener">${title}</a>`
                  : `<span class="bl-row__title">${title}</span>`
              }
              <span class="bl-row__tag">${escapeHtml(this.categoryLabels[item.category] ?? 'Other items')}</span>
              ${variantControl}
            </div>
            <label class="bl-row__field bl-row__field--price">
              <span class="bl-label">Value each</span>
              <input class="bl-input" type="number" inputmode="decimal" min="0" step="0.01" value="${item.price.toFixed(2)}" data-bl-price>
            </label>
            <label class="bl-row__field bl-row__field--qty">
              <span class="bl-label">Qty</span>
              <input class="bl-input" type="number" inputmode="numeric" min="1" max="${MAX_QTY}" step="1" value="${item.qty}" data-bl-qty>
            </label>
            <div class="bl-row__est">
              <small>Est. offer</small>
              <strong data-bl-est></strong>
            </div>
            <button type="button" class="bl-row__remove" data-bl-remove aria-label="Remove ${title}">
              <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">
                <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"></path>
              </svg>
            </button>
          </div>`;
      })
      .join('');

    this.refs.empty.hidden = items.length > 0;
  }

  /** Refreshes every number and dependent piece of UI without re-creating the rows (keeps input focus). */
  updateNumbers() {
    const { items } = this.state;
    const share = this.share;

    for (const row of this.refs.items.querySelectorAll('[data-uid]')) {
      const item = this.findItem(row.dataset.uid);
      const target = row.querySelector('[data-bl-est]');
      if (item && target) target.textContent = this.formatRange(this.estimate(item, share));
    }

    const hasItems = items.length > 0;
    this.refs.totals.hidden = !hasItems;
    this.refs.continueCta.hidden = !hasItems;

    if (hasItems) {
      const units = items.reduce((sum, item) => sum + item.qty, 0);
      const names = { cash: 'cash offer', credit: 'store credit offer', mixed: 'offer (cash and credit)' };
      this.refs.totalsLabel.textContent = `Estimated ${names[this.state.payout]} for ${units} ${units === 1 ? 'item' : 'items'}`;
      this.refs.totalsValue.textContent = this.formatRange(this.totals(share));
      this.refs.totalsCompare.textContent = `Cash ${this.formatRange(this.totals(0))} · Store credit ${this.formatRange(this.totals(1))}`;
    }

    this.renderChips();
    this.renderSummary();
  }

  /** Categories the seller ticked themselves. Nothing is ever ticked automatically (not even from their list). */
  activeCategories() {
    const picked = new Set(this.state.cats);
    return this.refs.chips.map((chip) => chip.value).filter((key) => picked.has(key));
  }

  renderChips() {
    const picked = new Set(this.state.cats);
    for (const chip of this.refs.chips) chip.checked = picked.has(chip.value);

    const active = new Set(this.activeCategories());
    for (const panel of this.refs.panels) {
      panel.hidden = !active.has(panel.dataset.blPanel);
    }
    if (this.refs.portalNote) this.refs.portalNote.hidden = !active.has('one_piece_singles');
  }

  renderSummary() {
    const { items } = this.state;
    if (!this.refs.summary) return;
    if (!items.length) {
      this.refs.summary.textContent = '';
      return;
    }
    const range = this.formatRange(this.totals(this.share));
    this.refs.summary.textContent = `Your list (${items.length} ${items.length === 1 ? 'line' : 'lines'}) will be sent with this request. Estimated offer: ${range}. Payout: ${this.payoutName()}.`;
  }

  /* ---------- Form ---------- */

  validate() {
    const active = this.activeCategories();
    if (!active.length && !this.state.items.length) {
      return 'Choose at least one category, or add something to your list above.';
    }

    const fromItems = new Set(this.state.items.map((item) => item.category));
    for (const panel of this.refs.panels) {
      if (panel.hidden || fromItems.has(panel.dataset.blPanel)) continue;
      const filled = [...panel.querySelectorAll('[data-bl-field]')].some((field) => field.value.trim() !== '');
      if (!filled) return `Tell us a little about your ${panel.dataset.title.toLowerCase()}.`;
    }
    return '';
  }

  buildBody() {
    const { items } = this.state;
    const share = this.share;
    const lines = ['BUYLIST REQUEST', ''];

    const ticked = this.activeCategories().map((key) => this.categoryLabels[key]);
    const listed = [...new Set(items.map((item) => this.categoryLabels[item.category]))];
    lines.push(`Categories: ${ticked.join(', ') || (listed.length ? `${listed.join(', ')} (from the list)` : 'None selected')}`);
    lines.push(`Preferred payout: ${this.payoutName()}`);

    const handover = this.refs.form.querySelector('input[name="bl-handover"]:checked');
    if (handover) lines.push(`Handover: ${handover.value}`);

    const photos = this.refs.form.querySelector('[data-bl-photos]')?.value.trim();
    if (photos) lines.push(`Photos: ${photos}`);

    if (items.length) {
      const base = this.config.productBase.replace(/\/$/, '');
      lines.push('', `ITEMS FROM THE ESTIMATOR (${items.length} ${items.length === 1 ? 'line' : 'lines'})`);
      items.forEach((item, index) => {
        const condition = this.variantLabel(item);
        const range = this.formatRange(this.estimate(item, share));
        lines.push(
          `${index + 1}. ${item.title}${condition ? ` [${condition}]` : ''} x${item.qty} @ ${this.money.format(item.price)} each => est. ${range} (${this.categoryLabels[item.category]})`
        );
        if (item.handle) lines.push(`   ${window.location.origin}${base}/products/${item.handle}`);
      });
      lines.push(`Estimated total (${this.payoutName()}): ${this.formatRange(this.totals(share))}`);
    }

    const fromItems = new Set(items.map((item) => item.category));
    for (const panel of this.refs.panels) {
      if (panel.hidden) continue;
      const fields = [...panel.querySelectorAll('[data-bl-field]')]
        .map((field) => [field.dataset.label, field.value.trim()])
        .filter(([, value]) => value !== '');
      if (!fields.length && fromItems.has(panel.dataset.blPanel)) continue;
      lines.push('', `-- ${panel.dataset.title} --`);
      for (const [label, value] of fields) lines.push(`${label}: ${value}`);
    }

    const notes = this.refs.form.querySelector('[data-bl-notes]')?.value.trim();
    if (notes) lines.push('', 'NOTES', notes);

    return lines.join('\n');
  }

  onSubmit(event) {
    const problem = this.validate();
    if (problem) {
      event.preventDefault();
      this.refs.error.textContent = problem;
      this.refs.error.hidden = false;
      this.refs.error.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return;
    }

    this.refs.error.hidden = true;
    this.refs.body.value = this.buildBody();
    this.refs.submit.disabled = true;
    this.refs.submit.dataset.label = this.refs.submit.textContent;
    this.refs.submit.textContent = 'Sending…';
  }

  resetSubmit() {
    const { submit } = this.refs;
    if (!submit || !submit.disabled) return;
    submit.disabled = false;
    if (submit.dataset.label) submit.textContent = submit.dataset.label;
  }
}

if (!customElements.get('buylist-request')) {
  customElements.define('buylist-request', BuylistRequest);
}
